import { LoginCredentials, RegisterData, AuthResponse, User } from '../types/auth';
import { supabase, UserProfile } from '../lib/supabase';

// Helper function to convert Supabase user to our User type
const convertSupabaseUser = (supabaseUser: any, profile?: UserProfile): User => {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    firstName: profile?.first_name || supabaseUser.user_metadata?.first_name || '',
    lastName: profile?.last_name || supabaseUser.user_metadata?.last_name || '',
    isVerified: supabaseUser.email_confirmed_at !== null,
    isAdmin: profile?.is_admin || false,
    createdAt: supabaseUser.created_at,
    updatedAt: supabaseUser.updated_at || supabaseUser.created_at,
  };
};

// Helper function to get user profile (database trigger handles creation)
const getUserProfile = async (userId: string, retries = 3): Promise<UserProfile | null> => {
  try {
    // Try to get existing profile with retries (in case trigger is still running)
    for (let i = 0; i < retries; i++) {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profile) {
        return profile;
      }

      if (error && !error.message.includes('No rows')) {
        console.error('Error fetching user profile:', error);
      }

      // Wait a bit before retrying (database trigger might still be running)
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // If still no profile after retries, log but don't fail
    console.warn('User profile not found after retries, user may need to refresh');
    return null;
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    return null;
  }
};

// Helper function to sync user to marketing database
const syncToMarketingDatabase = async (user: any, profile: UserProfile) => {
  try {
    const { error } = await supabase.functions.invoke('handle-registration', {
      body: { user, profile }
    });

    if (error) {
      console.error('Marketing sync error:', error);
    }
  } catch (error) {
    console.error('Error syncing to marketing database:', error);
  }
};

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const LOGIN_TIMEOUT = 10000; // 10 seconds max for entire login
    const DB_OPERATION_TIMEOUT = 3000; // 3 seconds per database operation
    
    // console.log('🔐 Starting login process for:', credentials.email);
    
    const loginPromise = async (): Promise<AuthResponse> => {
      // Step 1: Supabase authentication (this part is reliable)
      // console.log('🔐 Step 1: Authenticating with Supabase...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        console.error('🔐 Supabase auth failed:', error.message);
        throw new Error(error.message);
      }

      if (!data.user || !data.session) {
        console.error('🔐 Login failed: No user or session data');
        throw new Error('Login failed');
      }

      // console.log('🔐 Step 1 complete: Supabase auth successful');

      // Step 2: Create basic user object (always works)
      // console.log('🔐 Step 2: Creating basic user object...');
      let user = convertSupabaseUser(data.user);
      // console.log('🔐 Step 2 complete: Basic user object created');

      // Step 3: Try to enhance with profile data (with timeout protection)
      // console.log('🔐 Step 3: Attempting to fetch user profile...');
      try {
        const profilePromise = getUserProfile(data.user.id, 1);
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout')), DB_OPERATION_TIMEOUT)
        );
        
        const profile = await Promise.race([profilePromise, timeoutPromise]) as UserProfile | null;
        if (profile) {
          user = convertSupabaseUser(data.user, profile);
          // console.log('🔐 Step 3 complete: Profile data loaded successfully');
        } else {
          // console.log('🔐 Step 3: No profile found, using basic user data');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn('🔐 Step 3 failed/timed out:', errorMessage);
        // console.log('🔐 Continuing with basic user data...');
        // Continue with basic user data - this is not a fatal error
      }

      // Step 4: Schedule last_active update (non-blocking background operation)
      // console.log('🔐 Step 4: Scheduling last_active update...');
      setTimeout(async () => {
        try {
          const updatePromise = supabase
            .from('user_profiles')
            .update({ last_active: new Date().toISOString() })
            .eq('id', data.user.id);
          
          const timeoutPromise = new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Update timeout')), DB_OPERATION_TIMEOUT)
          );
          
          await Promise.race([updatePromise, timeoutPromise]);
          // console.log('🔐 Background: last_active updated successfully');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.warn('🔐 Background: last_active update failed:', errorMessage);
          // This is non-critical, so we don't throw
        }
      }, 0); // Execute in next tick (non-blocking)

      // console.log('🔐 Login process complete: Returning auth response');
      return {
        user,
        token: data.session.access_token,
        refreshToken: data.session.refresh_token,
      };
    };

    // Apply overall timeout to entire login process
    // console.log('🔐 Applying overall timeout protection...');
    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => {
        console.error('🔐 LOGIN TIMEOUT: Process exceeded', LOGIN_TIMEOUT, 'ms');
        reject(new Error('Login timeout - please try again. If this persists, check your internet connection.'));
      }, LOGIN_TIMEOUT)
    );

    try {
      const result = await Promise.race([loginPromise(), timeoutPromise]);
      // console.log('🔐 Login successful!');
      return result;
    } catch (error) {
      console.error('🔐 Login failed:', error);
      throw error;
    }
  },

  async register(data: RegisterData): Promise<void> {
    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`, // Auto-redirect to dashboard after verification
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    if (!authData.user) {
      throw new Error('Registration failed');
    }

    // Get user profile (database trigger should have created it)
    const profile = await getUserProfile(authData.user.id);

    // Sync to marketing database (non-blocking)
    if (profile) {
      try {
        await syncToMarketingDatabase(authData.user, profile);
      } catch (error) {
        console.warn('Marketing sync failed, but registration continues:', error);
      }
    }

    // console.log('Registration successful. Please check your email for verification.');
  },


  async verifyToken(token: string): Promise<User> {
    const { data: { user }, error } = await supabase.auth.getUser(token);

    if (error || !user) {
      throw new Error('Token verification failed');
    }

    // Get user profile
    const profile = await getUserProfile(user.id);
    return convertSupabaseUser(user, profile || undefined);
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: refreshToken,
    });

    if (error || !data.session || !data.user) {
      throw new Error('Token refresh failed');
    }

    // Get user profile
    const profile = await getUserProfile(data.user.id);
    const user = convertSupabaseUser(data.user, profile || undefined);

    return {
      user,
      token: data.session.access_token,
      refreshToken: data.session.refresh_token,
    };
  },

  async forgotPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw new Error(error.message);
    }
  },

  async resetPassword(_token: string, password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: password,
    });

    if (error) {
      throw new Error(error.message);
    }
  },

  // Helper method to check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  },

  // Helper method to get current user from Supabase
  async getCurrentUser(): Promise<User | null> {
    // console.log('🔐 AuthService: getCurrentUser() called');
    
    try {
      // console.log('🔐 AuthService: Calling supabase.auth.getUser()...');
      const { data: { user }, error } = await supabase.auth.getUser();
      
      // console.log('🔐 AuthService: getUser result:', {
      //   hasUser: !!user,
      //   hasError: !!error,
      //   userId: user?.id,
      //   userEmail: user?.email,
      //   errorMessage: error?.message
      // });
      
      if (error) {
        console.error('🔐 AuthService: Error getting user:', error);
        throw error;
      }
      
      if (!user) {
        // console.log('🔐 AuthService: No user found');
        return null;
      }

      // console.log('🔐 AuthService: User found, getting profile...');
      // Get user profile
      const profile = await getUserProfile(user.id);
      // console.log('🔐 AuthService: Profile result:', profile ? 'Found' : 'Not found');
      
      const convertedUser = convertSupabaseUser(user, profile || undefined);
      // console.log('🔐 AuthService: Converted user:', {
      //   id: convertedUser.id,
      //   email: convertedUser.email,
      //   isVerified: convertedUser.isVerified,
      //   isAdmin: convertedUser.isAdmin
      // });
      
      return convertedUser;
    } catch (error) {
      console.error('🔐 AuthService: getCurrentUser failed:', error);
      throw error;
    }
  },

  // Helper method to logout
  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Logout error:', error);
    }
  },

  // Get current session
  async getSession() {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  // Listen to auth state changes
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback);
  },
};
